/**
 * Ocesha UI translator (replaces Google's website translator widget, retired by Google
 * on Oct 1, 2026).
 *
 * Translates the interface text on the page (menus, buttons, labels, help texts) using
 * translations stored once per language in MongoDB (backend /api/i18n):
 *
 *   1. dictionary in the browser (localStorage)                      -> instant
 *   2. GET /api/i18n/bundle/<lang> (all stored strings, ETag/304)   -> no translation cost
 *   3. strings never translated before -> POST /api/i18n/translate  -> translated once by
 *      the Gemini service, stored, reused for every user afterwards
 *
 * Only strings that appear in the app's source code are translated (list built at
 * build time: public/i18n/ui-strings.json), so generated posts, blogs and user data
 * are never sent or stored. Text inside .notranslate / [translate="no"], inputs,
 * editors and code is never touched.
 *
 * React-safe: only the value of existing text nodes / attributes is changed (no
 * wrapper elements), and the English original is kept to restore or re-translate.
 */

const API_BASE = (process.env.REACT_APP_SERVER_ENDPOINT || "").replace(/\/+$/, "");
const I18N = `${API_BASE}/api/i18n`;
const ALLOWLIST_URL = `${process.env.PUBLIC_URL || ""}/i18n/ui-strings.json`;
const DICT_KEY = (lang: string) => `ui_i18n_${lang}`;
const ATTRS = ["placeholder", "title", "aria-label", "alt"];
const SKIP_SELECTOR = [
  "script", "style", "noscript", "textarea", "input", "select option", "code", "pre", "svg",
  "[contenteditable]", ".notranslate", "[translate='no']", ".ql-editor", ".ProseMirror",
  ".tox-tinymce", ".ck-editor", ".jodit-wysiwyg", ".DraftEditor-root",
].join(",");
const FIELD_SELECTOR = "input, textarea";
const FLUSH_MS = 250;
const MAX_BATCH = 100;

type Dict = Record<string, string>;

let currentLang = "en";
let dict: Dict = {};
let allow: Set<string> | null = null;
let allowPromise: Promise<void> | null = null;
let observer: MutationObserver | null = null;
const pending = new Set<string>();          // waiting to be sent
const inflight = new Set<string>();         // sent, no answer yet
const unavailable = new Set<string>();      // backend could not translate (remembered 1 day)
// Strings the backend could not translate are not asked for again on every page load
const MISS_KEY = (lang: string) => `ui_i18n_${lang}_miss`;
const MISS_TTL_MS = 24 * 60 * 60 * 1000;
const loadMisses = (lang: string) => {
  unavailable.clear();
  try {
    const m = JSON.parse(localStorage.getItem(MISS_KEY(lang)) || "{}") || {};
    const now = Date.now();
    Object.entries(m).forEach(([k, t]) => { if (now - Number(t) < MISS_TTL_MS) unavailable.add(k); });
  } catch { /* ignore */ }
};
const saveMisses = (lang: string, keys: string[]) => {
  if (!keys.length) return;
  try {
    const m = JSON.parse(localStorage.getItem(MISS_KEY(lang)) || "{}") || {};
    const now = Date.now();
    keys.forEach((k) => { m[k] = now; });
    Object.keys(m).forEach((k) => { if (now - Number(m[k]) >= MISS_TTL_MS) delete m[k]; });
    localStorage.setItem(MISS_KEY(lang), JSON.stringify(m));
  } catch { /* ignore */ }
};
let flushTimer: number | null = null;
let scanScheduled = false;
const dirtyRoots = new Set<Node>();

// Original English per text node / attribute, plus what we last wrote (to detect React updates)
const textState = new WeakMap<Text, { original: string; applied: string }>();
const attrState = new WeakMap<Element, Record<string, { original: string; applied: string }>>();

const norm = (s: string) => s.replace(/\s+/g, " ").trim();

const storage = {
  get(lang: string): Dict {
    try { return JSON.parse(localStorage.getItem(DICT_KEY(lang)) || "{}") || {}; } catch { return {}; }
  },
  set(lang: string, d: Dict) {
    try { localStorage.setItem(DICT_KEY(lang), JSON.stringify(d)); } catch { /* quota: ignore */ }
  },
};

// ------------------------------------------------------------------ data loading
const loadAllowlist = () => {
  if (!allowPromise) {
    allowPromise = fetch(ALLOWLIST_URL, { cache: "default" })
      .then((r) => (r.ok ? r.json() : { strings: [] }))
      .then((d) => { allow = new Set<string>(d?.strings || []); buildTemplates(d?.strings || []); })
      .catch(() => { allow = new Set<string>(); });
  }
  return allowPromise;
};

// ------------------------------------------------------------------ templates
// Text built at runtime ("2 Modules", "Set your preferences for "X" …") is matched against
// templates from the build-time list ("{0} Modules"). The template is translated once
// ("{0}モジュール") and the runtime values (numbers, names, titles) are put back unchanged.
type Template = { key: string; re: RegExp; order: number[] };
const PH = /\{(\d+)\}/g;
const IDX = 4;                                   // index by the first / last 4 characters
const byPrefix = new Map<string, Template[]>();
const bySuffix = new Map<string, Template[]>();
const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

function buildTemplates(strings: string[]) {
  byPrefix.clear(); bySuffix.clear();
  for (const key of strings) {
    if (key.indexOf("{") < 0) continue;
    const order: number[] = [];
    let src = "^", last = 0;
    key.replace(PH, (m, n, at) => {
      src += escapeRe(key.slice(last, at)) + "(.+?)";
      order.push(Number(n));
      last = at + m.length;
      return m;
    });
    if (!order.length) continue;
    src += escapeRe(key.slice(last)) + "$";
    const tpl: Template = { key, re: new RegExp(src, "s"), order };
    const prefix = key.slice(0, key.indexOf("{"));
    const suffix = key.slice(key.lastIndexOf("}") + 1);
    if (prefix.length >= IDX) {
      const k = prefix.slice(0, IDX).toLowerCase();
      byPrefix.set(k, [...(byPrefix.get(k) || []), tpl]);
    } else if (suffix.length >= IDX) {
      const k = suffix.slice(-IDX).toLowerCase();
      bySuffix.set(k, [...(bySuffix.get(k) || []), tpl]);
    }
  }
}

const matchTemplate = (key: string): { tpl: Template; values: string[] } | null => {
  const cands = [
    ...(byPrefix.get(key.slice(0, IDX).toLowerCase()) || []),
    ...(bySuffix.get(key.slice(-IDX).toLowerCase()) || []),
  ];
  for (const tpl of cands) {
    const m = tpl.re.exec(key);
    if (m) {
      const values: string[] = [];
      tpl.order.forEach((n, i) => { values[n] = m[i + 1]; });
      return { tpl, values };
    }
  }
  return null;
};

// A value that is itself an interface word (a chosen option like "professional" or
// "Business Leaders") is translated too; numbers, names and titles stay as they are.
const translateValue = (raw: string): string => {
  const v = raw.trim();
  if (!v || !/[A-Za-z]/.test(v) || v.length > 60) return raw;
  const cap = v.charAt(0).toUpperCase() + v.slice(1);
  for (const k of [v, cap]) {
    const hit = dict[k];
    if (hit) {
      // keep lower case where the sentence used it ("a professional article")
      const out = v === k || !/^[a-z]/.test(v) ? hit : hit.charAt(0).toLowerCase() + hit.slice(1);
      return raw.replace(v, out);
    }
    // a single capitalised word ("Rohan") is probably a name: only use an existing translation
    if (allow && allow.has(k) && !/^[A-Z][a-z]+$/.test(v)) { want(k); return raw; }
  }
  return raw;
};

const fillTemplate = (translated: string, values: string[]) =>
  translated.replace(PH, (m, n) => (values[Number(n)] != null ? translateValue(values[Number(n)]) : m));

const loadBundle = async (lang: string) => {
  try {
    const etagKey = `${DICT_KEY(lang)}_etag`;
    const etag = localStorage.getItem(etagKey) || "";
    const res = await fetch(`${I18N}/bundle/${encodeURIComponent(lang)}`, {
      headers: etag ? { "If-None-Match": etag } : {},
    });
    if (res.status === 304 || !res.ok) return;
    const data = await res.json();
    if (lang !== currentLang) return;
    dict = { ...dict, ...(data?.strings || {}) };
    storage.set(lang, dict);
    const newTag = res.headers.get("ETag");
    if (newTag) { try { localStorage.setItem(etagKey, newTag); } catch { /* ignore */ } }
    scheduleScan(document.body);
  } catch { /* offline / API down: keep what we have */ }
};

const flush = async () => {
  flushTimer = null;
  if (currentLang === "en" || !pending.size) return;
  const lang = currentLang;
  const batch = Array.from(pending).slice(0, MAX_BATCH);
  batch.forEach((s) => { pending.delete(s); inflight.add(s); });
  try {
    const res = await fetch(`${I18N}/translate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ lang, texts: batch }),
    });
    const data = res.ok ? await res.json() : { strings: {} };
    const got: Dict = data?.strings || {};
    const missed = batch.filter((s) => !got[s]);
    missed.forEach((s) => unavailable.add(s));
    if (res.ok) saveMisses(lang, missed);      // a real "no translation", not a network error
    if (lang === currentLang && Object.keys(got).length) {
      dict = { ...dict, ...got };
      storage.set(lang, dict);
      scheduleScan(document.body);
    }
  } catch {
    batch.forEach((s) => unavailable.add(s));
  } finally {
    batch.forEach((s) => inflight.delete(s));
    if (pending.size) scheduleFlush();
  }
};

const scheduleFlush = () => {
  if (flushTimer == null) flushTimer = window.setTimeout(flush, FLUSH_MS);
};

// ------------------------------------------------------------------ translating the DOM
// Elements marked data-i18n are always translated, even if their text is not in the
// build-time list (e.g. text composed at runtime like "Welcome back").
const FORCE_SELECTOR = "[data-i18n]";

const want = (key: string) => {
  if (key.length <= 300 && !inflight.has(key) && !unavailable.has(key)) {
    pending.add(key);
    scheduleFlush();
  }
};

const lookup = (original: string, forced = false): string | null => {
  const key = norm(original);
  if (!key) return null;
  // keep the original surrounding whitespace
  const wrap = (s: string) => (original.match(/^\s*/)?.[0] || "") + s + (original.match(/\s*$/)?.[0] || "");
  const hit = dict[key];
  if (hit) return wrap(hit);
  // Text with numbers ("2 Modules", "10 Modules") uses the shared template ("{0} Modules"),
  // so every count is translated the same way; other text uses its exact translation.
  const m = matchTemplate(key);
  if (m && (/\d/.test(key) || !(allow && allow.has(key)))) {
    const t = dict[m.tpl.key];
    if (t) return wrap(fillTemplate(t, m.values));
    want(m.tpl.key);
    return null;
  }
  if (forced || (allow && allow.has(key))) want(key);
  return null;
};

const skip = (el: Element | null) => !el || !!el.closest(SKIP_SELECTOR);

// <p>Welcome back {name}!</p> is rendered as 3 text nodes ("Welcome back ", "Saravanan", "!").
// They are translated together as one sentence (template "Welcome back {0}!").
const groupState = new WeakMap<Element, { nodes: Text[]; originals: string[]; applied: string[] }>();

const isTextGroup = (el: Element | null): boolean => {
  if (!el || el.childNodes.length < 2) return false;
  let words = 0;
  for (const c of Array.from(el.childNodes)) {
    if (c.nodeType !== Node.TEXT_NODE) return false;
    if (/[A-Za-z]/.test(c.nodeValue || "")) words++;
  }
  return words > 0;
};

const translateGroup = (el: Element) => {
  const nodes = Array.from(el.childNodes) as Text[];
  let gs = groupState.get(el);
  if (!gs || gs.nodes.length !== nodes.length || gs.nodes.some((n, i) => n !== nodes[i])) {
    gs = { nodes, originals: nodes.map((n) => n.nodeValue || ""), applied: nodes.map((n) => n.nodeValue || "") };
    groupState.set(el, gs);
  } else {
    nodes.forEach((n, i) => {                 // React changed one part (e.g. the name)
      const v = n.nodeValue || "";
      if (v !== gs!.applied[i]) { gs!.originals[i] = v; gs!.applied[i] = v; }
    });
  }
  let target = gs.originals;
  if (currentLang !== "en") {
    const joined = gs.originals.join("");
    const whole = lookup(joined, !!el.closest(FORCE_SELECTOR));
    const key = norm(joined);
    // the whole sentence is known (its translation is on the way): don't translate pieces
    const known = !!dict[key] || !!(allow && allow.has(key)) || !!matchTemplate(key);
    target = whole != null
      ? [whole, ...gs.originals.slice(1).map(() => "")]
      : known ? gs.originals
      : gs.originals.map((o) => (/[A-Za-z]/.test(o) ? (lookup(o) ?? o) : o));
  }
  nodes.forEach((n, i) => {
    if ((n.nodeValue || "") !== target[i]) { gs!.applied[i] = target[i]; n.nodeValue = target[i]; }
  });
};

const translateText = (node: Text) => {
  if (isTextGroup(node.parentElement)) { translateGroup(node.parentElement as Element); return; }
  const value = node.nodeValue || "";
  let st = textState.get(node);
  if (!st || value !== st.applied) {
    // first time, or React / the app changed the text since we last wrote it
    st = { original: value, applied: value };
    textState.set(node, st);
  }
  if (currentLang === "en") {
    if (value !== st.original) { st.applied = st.original; node.nodeValue = st.original; }
    return;
  }
  if (!/[A-Za-z]/.test(st.original)) return;
  const out = lookup(st.original, !!node.parentElement?.closest(FORCE_SELECTOR));
  const next = out ?? st.original;
  if (next !== value) {
    st.applied = next;
    node.nodeValue = next;
  }
};

const translateAttrs = (el: Element) => {
  let st = attrState.get(el);
  for (const a of ATTRS) {
    if (!el.hasAttribute(a)) continue;
    const value = el.getAttribute(a) || "";
    if (!st) { st = {}; attrState.set(el, st); }
    let s = st[a];
    if (!s || value !== s.applied) { s = { original: value, applied: value }; st[a] = s; }
    const next = currentLang === "en" ? s.original : (lookup(s.original) ?? s.original);
    if (next !== value) { s.applied = next; el.setAttribute(a, next); }
  }
};

const scan = (root: Node) => {
  if (root.nodeType === Node.TEXT_NODE) {
    if (!skip((root as Text).parentElement)) translateText(root as Text);
    return;
  }
  if (!(root instanceof Element) && root !== document.body) return;
  const base = root as Element;
  if (skip(base)) {
    if (base.matches(FIELD_SELECTOR) && !base.closest(".notranslate, [translate='no']")) translateAttrs(base);
    return;
  }
  const walker = document.createTreeWalker(base, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT, {
    acceptNode: (n) => {
      if (n.nodeType === Node.ELEMENT_NODE) {
        const el = n as Element;
        if (el.matches(SKIP_SELECTOR)) {
          // inputs / textareas: translate the placeholder & tooltip, never the value
          if (el.matches(FIELD_SELECTOR) && !el.closest(".notranslate, [translate='no']")) translateAttrs(el);
          return NodeFilter.FILTER_REJECT;
        }
        return NodeFilter.FILTER_ACCEPT;
      }
      return (n.nodeValue || "").trim() ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP;
    },
  });
  if (base.nodeType === Node.ELEMENT_NODE) translateAttrs(base);
  let n: Node | null = walker.nextNode();
  while (n) {
    if (n.nodeType === Node.TEXT_NODE) translateText(n as Text);
    else translateAttrs(n as Element);
    n = walker.nextNode();
  }
};

function scheduleScan(root: Node) {
  dirtyRoots.add(root);
  if (scanScheduled) return;
  scanScheduled = true;
  requestAnimationFrame(() => {
    scanScheduled = false;
    const roots = Array.from(dirtyRoots);
    dirtyRoots.clear();
    if (roots.includes(document.body)) scan(document.body);
    else roots.forEach((r) => { if (r.isConnected) scan(r); });
  });
}

const startObserver = () => {
  if (observer) return;
  observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      if (m.type === "characterData") {
        const t = m.target as Text;
        const st = textState.get(t);
        if (st && t.nodeValue === st.applied) continue;     // our own write
        const gs = t.parentElement ? groupState.get(t.parentElement) : undefined;
        if (gs && gs.nodes.includes(t) && t.nodeValue === gs.applied[gs.nodes.indexOf(t)]) continue;
        scheduleScan(t);
      } else if (m.type === "attributes") {
        const el = m.target as Element;
        const st = attrState.get(el)?.[m.attributeName || ""];
        if (st && el.getAttribute(m.attributeName || "") === st.applied) continue;
        scheduleScan(el);
      } else {
        m.addedNodes.forEach((n) => scheduleScan(n));
      }
    }
  });
  observer.observe(document.body, {
    subtree: true, childList: true, characterData: true,
    attributes: true, attributeFilter: ATTRS,
  });
};

// ------------------------------------------------------------------ public API
export const getUiLanguage = () => currentLang;

/** Switch the interface language live ("en" restores the original text). */
export const setUiLanguage = async (lang: string) => {
  currentLang = lang || "en";
  pending.clear();
  loadMisses(currentLang);
  if (currentLang === "en") {
    dict = {};
    scheduleScan(document.body);       // restore English
    return;
  }
  dict = storage.get(currentLang);
  startObserver();
  scheduleScan(document.body);         // instant from the browser dictionary
  // Shared DB dictionary first, so only strings nobody has translated yet are requested
  await Promise.all([loadAllowlist(), loadBundle(currentLang)]);
  scheduleScan(document.body);
};

/** Start on page load with the saved language. */
export const startUiTranslator = (lang: string) => {
  const run = () => setUiLanguage(lang);
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", run, { once: true });
  else run();
};
